/**
 * Initialize the mobile menu using Mmenu framework.
 * Configures theme, navbars, breadcrumbs, and hooks for dynamic site info.
 */
export const initMobileMenu = () => {
    const menu = document.getElementById('mobilemenu');
    if (!menu) return;

    const rawLang = pageLanguage() || '';
    const language = rawLang.split('-')[0];

    const translations =
        {
            da: { prefix: 'KU', url: 'https://www.ku.dk/', homeText: 'Forside' },
            en: { prefix: 'UCPH', url: 'https://www.ku.dk/en/', homeText: 'Home' }
        }[language] ||
        {
            prefix: 'UCPH',
            url: 'https://www.ku.dk/en/',
            homeText: 'Home'
        };

    const siteElement = document.querySelector('.sitename');
    const sitename = siteElement?.innerText || '';
    const sitelink = siteElement?.querySelector('a')?.getAttribute('href') || '#';

    // Danish translations
    if (language === 'da') {
        Mmenu.i18n({
            'Menu': sitename,
            'Open submenu': 'Åbn menu',
            'Close submenu': 'Luk menu'
        }, 'da');
    }

    new Mmenu('#mobilemenu', {
        theme: 'white',
        setSelected: { parent: true, current: 'detect' },
        offCanvas: { position: 'right-front' },
        navbars: [
            { position: 'top', content: ['<span class="current-subsite"></span>', 'close'] },
            { position: 'top', content: ['breadcrumbs'] },
            { content: ['prev', '<div class="parent"></div>'] }
        ],
        hooks: {
            'initMenu:after': () => handleInitMenuAfter(sitename, sitelink),
            'openPanel:after': () => handleOpenPanelAfter(sitename, sitelink, translations, language)
        }
    }, {
        language,
        navbars: { breadcrumbs: { removeFirst: false } }
    });
}

/**
 * Handle the `initMenu:after` hook.
 * Inserts the site name link into the top navbar.
 *
 * @param {string} sitename - The name of the current site/subsite.
 * @param {string} sitelink - The URL to the site root.
 */
const handleInitMenuAfter = (sitename, sitelink) => {
    if (!sitename) return;
    const currentSite = document.querySelector('.current-subsite');
    currentSite.innerHTML = `<a id="mobile-menu-header" href="${sitelink}" 
        data-category-content-presentation="Mobile menu site name" 
        data-category-content-name="${sitename}">${sitename}</a>`;
}

/**
 * Handle the `openPanel:after` hook.
 * Updates breadcrumbs, parent link, static prefix, and scroll behavior.
 *
 * @param {string} sitename - The name of the current site/subsite.
 * @param {string} sitelink - The URL to the site root.
 * @param {Object} translations - Language-specific translations and prefix info.
 * @param {string} translations.prefix - The static prefix to display in breadcrumbs.
 * @param {string} translations.url - The root URL for the prefix link.
 * @param {string} translations.homeText - Text for the site root/home breadcrumb.
 * @param {string} language - The current page language ('da' or 'en').
 */
const handleOpenPanelAfter = (sitename, sitelink, translations, language) => {
    if (!translations) return;

    const parentContainer = document.querySelector('.parent');
    const crumbs = document.querySelector('.mm-navbar__breadcrumbs');
    const breadcrumbItems = Array.from(crumbs.querySelectorAll('a'));

    // --- Determine the parent item ---
    let parentItem;
    if (breadcrumbItems.length === 1) {
        parentItem = document.createElement('a');
        parentItem.href = sitelink;
        parentItem.dataset.categoryContentName = translations.homeText;
        parentItem.dataset.categoryContentPresentation = 'Subsite root';
        parentItem.textContent = translations.homeText;
    } else {
        parentItem = breadcrumbItems[breadcrumbItems.length - 1].cloneNode(true);
    }

    parentContainer.innerHTML = '';
    parentContainer.appendChild(parentItem);

    // --- Fix English menu name bug before prepend (index is stable here) ---
    if (language === 'en' && crumbs.children[0]?.textContent === 'Menu') {
        crumbs.children[0].textContent = sitename;
    }

    // --- Build prefix fragment: KU root + custom level items, each with a trailing separator ---
    const fragment = document.createDocumentFragment();

    const appendCrumbLink = (href, text, presentation) => {
        const a = document.createElement('a');
        a.href = href;
        a.dataset.categoryContentName = text;
        a.dataset.categoryContentPresentation = presentation;
        a.textContent = text;
        fragment.appendChild(a);
        const sep = document.createElement('span');
        sep.className = 'mm-separator';
        sep.textContent = '/';
        fragment.appendChild(sep);
    };

    const showRoot = document.querySelector('.crumbs')?.dataset.showRoot !== '0';
    if (showRoot) {
        appendCrumbLink(translations.url, translations.prefix, 'Mobile root name');
    }

    document.querySelectorAll('.crumbs [data-level-item] a').forEach(levelLink => {
        appendCrumbLink(levelLink.href, levelLink.textContent.trim(), 'Mobile breadcrumb level');
    });

    crumbs.prepend(fragment);

    // --- Scroll last breadcrumb item into view ---
    const lastCrumb = crumbs.querySelector('a:last-child');
    lastCrumb?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
}

/**
 * Detect mobile menu toggle and reload page if necessary.
 * Should be called on window resize to handle anchor navigation.
 */
export const mobileMenuDetect = () => {
    const toggle = document.querySelector('.navbar-toggler');
    if (!toggle) return;

    if (!toggle.classList.contains('resized')) {
        toggle.classList.add('resized');
        toggle.addEventListener('click', () => {
            const anchor = toggle.getAttribute('href');
            if (window.location.hash !== anchor) {
                window.location.hash = anchor;
                window.location.reload();
            }
        });
    }
}
